import { getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import { rectsEqual } from "@/lib/math/rect";

/**
 * Expand a command's changed-node seed with every node whose bounds or rotation
 * drifted between `previous` and `next`.
 *
 * Auto-layout settle often moves/resizes ancestors and descendants that were
 * never listed on the command (nested flex/pattern children, hug frames).
 * Retained-scene incremental reuse keys off this set, so omitting those ids
 * leaves stale world-space geometry after undo/redo.
 */
export function expandChangedNodeIdsWithGeometryDrift(
  previous: CurvaDocument,
  next: CurvaDocument,
  seed: Iterable<string> = [],
): string[] {
  const ids = new Set(seed);
  const previousById = new Map(getAllSceneNodes(previous).map(node => [node.id, node] as const));

  for (const node of getAllSceneNodes(next)) {
    const prior = previousById.get(node.id);
    if (!prior) {
      ids.add(node.id);
      continue;
    }
    if (
      !rectsEqual(prior.bounds, node.bounds)
      || Math.abs((prior.transform.rotation ?? 0) - (node.transform.rotation ?? 0)) > 0.0001
    ) {
      ids.add(node.id);
    }
  }

  return [...ids];
}
